import { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  url: string;
  title?: string;
  onClose?: () => void;
}

export default function PDFViewer({ url, title, onClose }: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setLoading(false);
    setError(false);
  };

  const onDocumentLoadError = () => {
    setLoading(false);
    setError(true);
  };

  const handleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  // Disable right-click on the viewer
  const handleContextMenu = (e: React.MouseEvent) => e.preventDefault();

  const prevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const nextPage = () => setCurrentPage((p) => Math.min(numPages, p + 1));
  const zoomIn = () => setScale((s) => Math.min(2.5, s + 0.15));
  const zoomOut = () => setScale((s) => Math.max(0.5, s - 0.15));

  return (
    <div
      ref={containerRef}
      className="flex flex-col w-full h-full"
      style={{ background: '#0D0F12', minHeight: isFullscreen ? '100vh' : undefined }}
    >
      {/* Toolbar */}
      <div
        className="flex items-center justify-between px-5 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(248,246,243,0.07)', background: '#16181C' }}
      >
        {/* Left: page nav */}
        <div className="flex items-center gap-2">
          <button
            onClick={prevPage}
            disabled={currentPage <= 1}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer disabled:opacity-30"
            style={{ background: 'rgba(248,246,243,0.06)' }}
          >
            <i className="ri-arrow-left-s-line text-[#F8F6F3]/70 text-base" />
          </button>
          <span className="text-[#F8F6F3]/50 text-xs px-2">
            {currentPage} / {numPages || '—'}
          </span>
          <button
            onClick={nextPage}
            disabled={currentPage >= numPages}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer disabled:opacity-30"
            style={{ background: 'rgba(248,246,243,0.06)' }}
          >
            <i className="ri-arrow-right-s-line text-[#F8F6F3]/70 text-base" />
          </button>
        </div>

        {/* Center: title */}
        {title && (
          <p className="text-[#F8F6F3]/60 text-xs truncate max-w-xs">{title}</p>
        )}

        {/* Right: zoom + fullscreen + close */}
        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer hover:opacity-70"
            style={{ background: 'rgba(248,246,243,0.06)' }}
          >
            <i className="ri-zoom-out-line text-[#F8F6F3]/70 text-sm" />
          </button>
          <span className="text-[#F8F6F3]/40 text-xs w-12 text-center">{Math.round(scale * 100)}%</span>
          <button
            onClick={zoomIn}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer hover:opacity-70"
            style={{ background: 'rgba(248,246,243,0.06)' }}
          >
            <i className="ri-zoom-in-line text-[#F8F6F3]/70 text-sm" />
          </button>
          <div className="w-px h-5 mx-1" style={{ background: 'rgba(248,246,243,0.10)' }} />
          <button
            onClick={handleFullscreen}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer hover:opacity-70"
            style={{ background: 'rgba(248,246,243,0.06)' }}
          >
            <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-[#F8F6F3]/70 text-sm`} />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer hover:opacity-70"
              style={{ background: 'rgba(248,246,243,0.06)' }}
            >
              <i className="ri-close-line text-[#F8F6F3]/70 text-sm" />
            </button>
          )}
        </div>
      </div>

      {/* PDF Canvas area */}
      <div
        ref={viewerRef}
        className="flex-1 overflow-auto flex justify-center py-6 px-4"
        style={{ background: '#1A1A1F' }}
        onContextMenu={handleContextMenu}
      >
        {loading && !error && (
          <div className="flex items-center justify-center w-full py-20">
            <i className="ri-loader-4-line animate-spin text-[#8B9D83] text-2xl" />
          </div>
        )}
        {error && (
          <div className="flex flex-col items-center justify-center w-full py-20 gap-4">
            <i className="ri-file-damage-line text-[#C97B63]/50 text-4xl" />
            <p className="text-[#F8F6F3]/40 text-sm">No se pudo cargar el documento</p>
          </div>
        )}
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={null}
          className="flex flex-col gap-4 items-center"
        >
          <Page
            pageNumber={currentPage}
            scale={scale}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="shadow-2xl"
          />
        </Document>
      </div>

      {/* Bottom page indicator for fullscreen */}
      {isFullscreen && numPages > 0 && (
        <div
          className="flex items-center justify-center gap-4 py-3 flex-shrink-0"
          style={{ background: '#16181C', borderTop: '1px solid rgba(248,246,243,0.07)' }}
        >
          <button onClick={prevPage} disabled={currentPage <= 1} className="px-4 py-1.5 rounded-lg text-xs cursor-pointer disabled:opacity-30 whitespace-nowrap" style={{ background: 'rgba(248,246,243,0.06)', color: 'rgba(248,246,243,0.60)' }}>
            ← Anterior
          </button>
          <span className="text-[#F8F6F3]/40 text-xs">{currentPage} / {numPages}</span>
          <button onClick={nextPage} disabled={currentPage >= numPages} className="px-4 py-1.5 rounded-lg text-xs cursor-pointer disabled:opacity-30 whitespace-nowrap" style={{ background: 'rgba(248,246,243,0.06)', color: 'rgba(248,246,243,0.60)' }}>
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
}
